import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { FormattedMessage } from 'react-intl'
import { useSelector } from 'react-redux'
import { useRouteMatch } from 'react-router-dom'

import { INBOX_FILTER } from '@constants'
import { IApplicationState } from '@store'
import { selectCampaignId, selectCurrentView, selectLocationsearchAsObject } from '@store/router'
import { selectAllSubmissionIds } from '@store/submissions'
import { dismissToast, toast } from '@tribegroup'

interface ISelectedSubmissionsState {
  isSelectionModeOn: boolean
  selectedSubmissionIds: ReadonlyArray<number>
}

export interface ISelectedSubmissionsContextProps extends ISelectedSubmissionsState {
  selectSubmission: (submissionId: number) => void
  deselectSubmission: (submissionId: number) => void
  emptySelection: VoidFunction
  selectAll: VoidFunction
  toggleSelectionMode: VoidFunction
  closeSelectionMode: VoidFunction
  hideToast: VoidFunction
}

interface ISelectedSubmissionsProviderProps {
  allSubmissionIds?: ReadonlyArray<number>
  selectedFilter: string
  currentView: string
}

export const SelectedSubmissionsContext = createContext({})

// Base component for testing
export const SelectedSubmissionsProvider: React.FC<
  PropsWithChildren<ISelectedSubmissionsProviderProps>
> = ({ children, allSubmissionIds, selectedFilter, currentView }) => {
  const [isSelectionModeOn, setIsSelectionModeOn] = useState(false)
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState<ReadonlyArray<number>>([])

  const prevSelectionModeRef = useRef(false)
  const prevCurrentViewRef = useRef(currentView)

  useEffect(() => {
    // Handle selection mode changes (toast logic)
    if (!prevSelectionModeRef.current && isSelectionModeOn) {
      const filter = selectedFilter || INBOX_FILTER
      if (filter === INBOX_FILTER) {
        toast(<FormattedMessage id="inbox.cardlist.selectionMode.toast" />, false)
      }
    } else if (!isSelectionModeOn && selectedSubmissionIds.length !== 0) {
      dismissToast()
    }
    prevSelectionModeRef.current = isSelectionModeOn
  }, [isSelectionModeOn, selectedFilter, selectedSubmissionIds.length])

  useEffect(() => {
    // Reset selection state when the current view changes.
    if (prevCurrentViewRef.current !== currentView) {
      setIsSelectionModeOn(false)
      setSelectedSubmissionIds([])
      prevCurrentViewRef.current = currentView
    }
  }, [currentView])

  const toggleSelectionMode = useCallback(() => {
    setIsSelectionModeOn((prev) => !prev)
    setSelectedSubmissionIds([])
  }, [])

  const hideToast = useCallback(() => {
    dismissToast()
  }, [])

  const closeSelectionMode = useCallback(() => {
    setIsSelectionModeOn(false)
    setSelectedSubmissionIds([])
  }, [])

  const selectSubmission = useCallback((submissionId: number) => {
    setSelectedSubmissionIds((prev) => [...prev, submissionId])
  }, [])

  const deselectSubmission = useCallback((submissionId: number) => {
    setSelectedSubmissionIds((prev) => prev.filter((id) => id !== submissionId))
  }, [])

  const emptySelection = useCallback(() => {
    setSelectedSubmissionIds([])
  }, [])

  const selectAll = useCallback(() => {
    setSelectedSubmissionIds(allSubmissionIds || [])
  }, [allSubmissionIds])

  const contextProps: ISelectedSubmissionsContextProps = useMemo(
    () => ({
      selectSubmission,
      deselectSubmission,
      toggleSelectionMode,
      closeSelectionMode,
      isSelectionModeOn,
      selectedSubmissionIds,
      emptySelection,
      selectAll,
      hideToast,
    }),
    [
      selectSubmission,
      deselectSubmission,
      toggleSelectionMode,
      closeSelectionMode,
      isSelectionModeOn,
      selectedSubmissionIds,
      emptySelection,
      selectAll,
      hideToast,
    ],
  )

  return (
    <SelectedSubmissionsContext.Provider value={contextProps}>
      {children}
    </SelectedSubmissionsContext.Provider>
  )
}

// Connected component using hooks
const ConnectedSelectedSubmissionsProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const match = useRouteMatch()
  const searchParams = useSelector(selectLocationsearchAsObject)
  const campaignId = useSelector(() => selectCampaignId(match) as number)
  const currentView = useSelector(selectCurrentView)

  const selectedFilter = searchParams.filter && decodeURIComponent(searchParams.filter)
  const allSubmissionIds = useSelector((state: IApplicationState) =>
    selectAllSubmissionIds(state, campaignId!, selectedFilter),
  )

  return (
    <SelectedSubmissionsProvider
      selectedFilter={selectedFilter}
      allSubmissionIds={allSubmissionIds}
      currentView={currentView}
    >
      {children}
    </SelectedSubmissionsProvider>
  )
}

export default ConnectedSelectedSubmissionsProvider
